/**
 * Shared mechanical-equipment presentation helpers: plan glyph footprints and
 * colors, kept next to the other trades' helpers (../mep/ducts, ../mep/markers,
 * ../mep/raceways) so the 2D PlanView glyphs and the 3D MechanicalLayer glyphs
 * stay visually consistent from one source.
 */
import type { MechanicalKind } from "../api/types";

/**
 * Plan glyph fill per kind — the hex equivalents of the 3D
 * MechanicalLayer's MECHANICAL_COLOR, so an element reads as the same
 * equipment in both surfaces.
 */
export const MECHANICAL_PLAN_COLOR: Record<MechanicalKind, string> = {
  rooftop_unit: "#8b5cf6", // violet — roof plant
  air_handling_unit: "#06b6d4", // cyan — indoor air plant
  pump: "#10b981", // emerald — hydronic
  vav_terminal: "#a3e635", // lime — zone terminal
};

/**
 * Half-extent (plan meters) of a kind's glyph rectangle: half the catalog
 * footprint for real equipment, and a small fixed marker for the in-duct VAV
 * terminal (which has no plan footprint). Matches MECHANICAL_SIZE in
 * ../viewer/MechanicalLayer so plan and 3D glyphs are the same size.
 */
const MECHANICAL_GLYPH_HALF: Record<MechanicalKind, [number, number]> = {
  rooftop_unit: [0.9, 1.2],
  air_handling_unit: [0.75, 1.5],
  pump: [0.3, 0.45],
  vav_terminal: [0.3, 0.3],
};

/** Half-extent `[halfWidth, halfHeight]` in plan meters for a kind's glyph. */
export function mechanicalGlyphHalf(kind: MechanicalKind): [number, number] {
  return MECHANICAL_GLYPH_HALF[kind] ?? [0.3, 0.3];
}
